import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Clock,
  Download,
  Loader2,
  CalendarRange,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchTimeEntries, formatUsd } from '@/lib/timeEntriesData';

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => toIso(new Date());

/**
 * Returns the Monday → Sunday window that contains `today`, ISO-formatted.
 * Matches the convention used by `startOfThisWeekIso` in timeEntriesData.
 */
const thisWeekDefault = () => {
  const d = new Date();
  const dow = d.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? 6 : dow - 1; // 0=Mon..6=Sun
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
  return { fromDate: toIso(monday), toDate: toIso(d) };
};

const fmtHm = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Sum effective break minutes from the `breaks` JSONB column of a time entry.
 * Each break entry is `{ start, end }` (both ISO timestamps). An open break
 * is closed at clock_out_at.
 */
const breakMinutes = (entry) => {
  const breaks = Array.isArray(entry?.breaks) ? entry.breaks : [];
  let sec = 0;
  for (const b of breaks) {
    const startMs = b?.start ? new Date(b.start).getTime() : NaN;
    const endMs = b?.end
      ? new Date(b.end).getTime()
      : entry?.clock_out_at
      ? new Date(entry.clock_out_at).getTime()
      : NaN;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    sec += Math.max(0, Math.floor((endMs - startMs) / 1000));
  }
  return Math.round(sec / 60);
};

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const triggerDownload = (filename, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * HoursExportDialog — per-employee payroll CSV export.
 *
 * Filters `time_entries` by `crew_id` + date range, displays a quick preview
 * total (count + hours + pay), and downloads a CSV ready for a payroll
 * service. Columns: Date, Clock In, Clock Out, Break (min), Total Hours,
 * Hourly Rate, Total Pay.
 */
export const HoursExportDialog = ({ open, onOpenChange, crewMember }) => {
  const { t } = useTranslation();
  const defaults = useMemo(() => thisWeekDefault(), []);
  const [fromDate, setFromDate] = useState(defaults.fromDate);
  const [toDate, setToDate] = useState(defaults.toDate);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const crewId = crewMember?.id;
  const crewName = crewMember?.name || '—';
  const crewRate = Number(crewMember?.rate) || 0;

  const load = useCallback(async () => {
    if (!crewId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchTimeEntries({
        crewId,
        fromDate,
        toDate,
      });
      // Only completed entries (clock_out_at is set) count toward payroll;
      // an active entry has no end yet so its hours/cost are 0 in the row.
      const filtered = (list || []).filter((e) => e?.status !== 'cancelled');
      setEntries(filtered);
    } catch (e) {
      setError(e?.message || t('settings.crewHours.loadFailed'));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [crewId, fromDate, toDate, t]);

  // Reload whenever the dialog opens for a new employee or the date range
  // changes (debounced via the controlled inputs — direct date changes are
  // fast enough that an explicit "Refresh" button is unnecessary).
  useEffect(() => {
    if (open && crewId) load();
  }, [open, crewId, load]);

  // Reset to the default Mon→today range when reopened on a new employee.
  useEffect(() => {
    if (open && crewId) {
      setFromDate(defaults.fromDate);
      setToDate(defaults.toDate);
    }
  }, [open, crewId, defaults]);

  const totals = useMemo(() => {
    let hours = 0;
    let pay = 0;
    let completed = 0;
    for (const e of entries) {
      const h = Number(e?.hours) || 0;
      const c = Number(e?.cost) || 0;
      hours += h;
      pay += c;
      if (e?.clock_out_at) completed += 1;
    }
    return {
      hours: Math.round(hours * 100) / 100,
      pay: Math.round(pay * 100) / 100,
      total: entries.length,
      completed,
    };
  }, [entries]);

  const handleExport = () => {
    if (!entries.length) {
      toast.error(t('settings.crewHours.nothingToExport'));
      return;
    }
    setExporting(true);
    try {
      const header = [
        t('settings.crewHours.csv.employee'),
        t('settings.crewHours.csv.date'),
        t('settings.crewHours.csv.clockIn'),
        t('settings.crewHours.csv.clockOut'),
        t('settings.crewHours.csv.breakMin'),
        t('settings.crewHours.csv.totalHours'),
        t('settings.crewHours.csv.hourlyRate'),
        t('settings.crewHours.csv.totalPay'),
        t('settings.crewHours.csv.job'),
      ];
      const rows = entries.map((e) => [
        crewName,
        e?.date || '',
        fmtHm(e?.clock_in_at),
        fmtHm(e?.clock_out_at),
        breakMinutes(e),
        Number(e?.hours) || 0,
        crewRate,
        Number(e?.cost) || 0,
        e?.job || e?.job_id || '',
      ]);
      // Trailing summary row for the payroll service
      rows.push([
        '',
        '',
        '',
        '',
        '',
        totals.hours,
        crewRate,
        totals.pay,
        t('settings.crewHours.csv.summaryRow'),
      ]);
      const csv =
        header.map(csvEscape).join(',') +
        '\n' +
        rows.map((r) => r.map(csvEscape).join(',')).join('\n') +
        '\n';
      const slug = String(crewName || 'employee')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-+|-+$)/g, '');
      const filename = `hours-${slug}-${fromDate}-to-${toDate}.csv`;
      triggerDownload(filename, csv);
      toast.success(
        t('settings.crewHours.exportedToast', {
          count: entries.length,
          filename,
        })
      );
    } catch (e) {
      toast.error(e?.message || t('settings.crewHours.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  if (!crewMember) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-border max-w-2xl"
        data-testid="hours-export-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Clock size={16} className="text-[hsl(var(--primary))]" />
            {t('settings.crewHours.title', { name: crewName })}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t('settings.crewHours.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {/* Date range pickers */}
        <div className="grid grid-cols-2 gap-3" data-testid="hours-export-range">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
              <CalendarRange size={11} />
              {t('settings.crewHours.fromDate')}
            </Label>
            <Input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-10 mt-1 bg-[hsl(var(--background))] border-border text-foreground"
              data-testid="hours-from-date"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
              <CalendarRange size={11} />
              {t('settings.crewHours.toDate')}
            </Label>
            <Input
              type="date"
              value={toDate}
              min={fromDate}
              max={todayIso()}
              onChange={(e) => setToDate(e.target.value)}
              className="h-10 mt-1 bg-[hsl(var(--background))] border-border text-foreground"
              data-testid="hours-to-date"
            />
          </div>
        </div>

        <Separator className="bg-border" />

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-2" data-testid="hours-export-summary">
          <StatChip
            label={t('settings.crewHours.summary.entries')}
            value={totals.total}
            testId="hours-stat-entries"
          />
          <StatChip
            label={t('settings.crewHours.summary.totalHours')}
            value={`${totals.hours.toFixed(2)} h`}
            testId="hours-stat-hours"
          />
          <StatChip
            label={t('settings.crewHours.summary.totalPay')}
            value={formatUsd(totals.pay)}
            tone="success"
            testId="hours-stat-pay"
          />
        </div>

        {/* Preview list */}
        <section className="border border-border rounded-md bg-[hsl(var(--muted))]/30 max-h-72 overflow-y-auto">
          {error ? (
            <div className="px-3 py-4 text-xs text-[hsl(var(--destructive))] flex items-center gap-2">
              <AlertCircle size={13} />
              {error}
            </div>
          ) : loading ? (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">
              <Loader2
                size={14}
                className="inline-block animate-spin mr-2 align-text-bottom"
              />
              {t('settings.crewHours.loading')}
            </div>
          ) : entries.length === 0 ? (
            <div
              className="px-3 py-6 text-xs text-muted-foreground text-center"
              data-testid="hours-empty"
            >
              {t('settings.crewHours.empty')}
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="hours-preview-table">
              <thead className="sticky top-0 bg-[hsl(var(--card))] border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  <th className="text-left px-3 py-2">
                    {t('settings.crewHours.col.date')}
                  </th>
                  <th className="text-left px-3 py-2">
                    {t('settings.crewHours.col.in')}
                  </th>
                  <th className="text-left px-3 py-2">
                    {t('settings.crewHours.col.out')}
                  </th>
                  <th className="text-right px-3 py-2">
                    {t('settings.crewHours.col.hours')}
                  </th>
                  <th className="text-right px-3 py-2">
                    {t('settings.crewHours.col.pay')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-t border-border/60 hover:bg-[hsl(var(--muted))]/40"
                    data-testid={`hours-row-${e.id}`}
                  >
                    <td className="px-3 py-1.5 tabular-nums">{e.date}</td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {fmtHm(e.clock_in_at)}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                      {fmtHm(e.clock_out_at) || (
                        <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--warning))]">
                          {t('settings.crewHours.openEntry')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {(Number(e.hours) || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatUsd(Number(e.cost) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Badge
            variant="outline"
            className="text-[10px] uppercase tracking-[0.16em] border-border"
          >
            {t('settings.crewHours.fileNameHint', {
              filename: `hours-…-${fromDate}-to-${toDate}.csv`,
            })}
          </Badge>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={exporting}
            className="text-muted-foreground"
            data-testid="hours-close-button"
          >
            {t('common.close')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || loading || entries.length === 0}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="hours-export-button"
          >
            {exporting ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <Download size={13} className="mr-1.5" />
            )}
            {t('settings.crewHours.exportCsv')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatChip = ({ label, value, tone, testId }) => (
  <div
    className="border border-border rounded-md bg-[hsl(var(--card))] px-3 py-2"
    data-testid={testId}
  >
    <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={
        'text-lg font-semibold tabular-nums ' +
        (tone === 'success' ? 'text-[hsl(var(--success))]' : 'text-foreground')
      }
    >
      {value}
    </div>
  </div>
);

export default HoursExportDialog;
